import { useState, useEffect } from 'react'
import { Heart, Database, Cpu, HardDrive, Wifi, AlertTriangle } from 'lucide-react'

interface HealthDashboardProps {
  status: {
    memory: string
    qdrant: string
    ollama: string
    camera: string
    microphone: string
  }
}

interface ServiceStatus {
  name: string
  status: 'healthy' | 'degraded' | 'unhealthy'
  description: string
  icon: React.ComponentType<any>
  metrics?: { label: string; value: string }[]
}

function HealthDashboard({ status }: HealthDashboardProps) {
  const [services, setServices] = useState<ServiceStatus[]>([])

  useEffect(() => {
    // Simulate service status data
    const serviceData: ServiceStatus[] = [
      {
        name: 'Memory System',
        status: status.memory as 'healthy' | 'degraded' | 'unhealthy',
        description: 'Vector memory storage and retrieval',
        icon: Database,
        metrics: [
          { label: 'Vectors Stored', value: '1,247' },
          { label: 'Query Speed', value: '45ms' },
          { label: 'Accuracy', value: '98.7%' }
        ]
      },
      {
        name: 'Qdrant Database',
        status: status.qdrant as 'healthy' | 'degraded' | 'unhealthy',
        description: 'Vector database for semantic search',
        icon: HardDrive,
        metrics: [
          { label: 'Port', value: '6333' },
          { label: 'Collections', value: '3' },
          { label: 'Health', value: 'Healthy' }
        ]
      },
      {
        name: 'Ollama AI',
        status: status.ollama as 'healthy' | 'degraded' | 'unhealthy',
        description: 'Local AI model inference engine',
        icon: Cpu,
        metrics: [
          { label: 'Models Loaded', value: '5' },
          { label: 'Response Time', value: '1.2s' },
          { label: 'Memory Usage', value: '8.2GB' }
        ]
      },
      {
        name: 'Camera Interface',
        status: status.camera === 'available' ? 'healthy' : 'unhealthy',
        description: 'Real-time vision processing',
        icon: Wifi,
        metrics: [
          { label: 'Resolution', value: '1080p' },
          { label: 'FPS', value: '30' },
          { label: 'Status', value: 'Available' }
        ]
      },
      {
        name: 'Microphone',
        status: status.microphone === 'available' ? 'healthy' : 'unhealthy',
        description: 'Voice input processing',
        icon: Wifi,
        metrics: [
          { label: 'Sample Rate', value: '44.1kHz' },
          { label: 'Channels', value: '2' },
          { label: 'Status', value: 'Available' }
        ]
      }
    ]

    setServices(serviceData)
  }, [status])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'bg-green-500'
      case 'degraded': return 'bg-yellow-500'
      case 'unhealthy': return 'bg-red-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'healthy': return 'Healthy'
      case 'degraded': return 'Degraded'
      case 'unhealthy': return 'Unhealthy'
      default: return 'Unknown'
    }
  }

  const overallHealth = services.every(s => s.status === 'healthy') 
    ? 'healthy' 
    : services.some(s => s.status === 'unhealthy') 
      ? 'unhealthy' 
      : 'degraded'

  return (
    <div className="h-full flex flex-col bg-secondary-900 p-6">
      {/* Overall Health Status */}
      <div className={`rounded-lg p-6 mb-6 ${
        overallHealth === 'healthy' ? 'bg-green-500/20 border border-green-500/30' :
        overallHealth === 'degraded' ? 'bg-yellow-500/20 border border-yellow-500/30' :
        'bg-red-500/20 border border-red-500/30'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Heart className={`w-8 h-8 ${
              overallHealth === 'healthy' ? 'text-green-400' :
              overallHealth === 'degraded' ? 'text-yellow-400' :
              'text-red-400'
            }`} />
            <div>
              <h2 className="text-2xl font-bold">System Health</h2>
              <p className="text-secondary-300">
                {overallHealth === 'healthy' ? 'All systems operational' :
                 overallHealth === 'degraded' ? 'Some systems experiencing issues' :
                 'Critical systems unavailable'}
              </p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full text-white font-semibold ${
            overallHealth === 'healthy' ? 'bg-green-500' :
            overallHealth === 'degraded' ? 'bg-yellow-500' :
            'bg-red-500'
          }`}>
            {getStatusText(overallHealth).toUpperCase()}
          </div>
        </div>
      </div>

      {/* Service Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {services.map((service, index) => (
          <div key={index} className="bg-secondary-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status)}`} />
                <service.icon className="w-6 h-6 text-accent-400" />
                <h3 className="text-lg font-semibold">{service.name}</h3>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                service.status === 'healthy' ? 'bg-green-500/20 text-green-400' :
                service.status === 'degraded' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {getStatusText(service.status)}
              </span>
            </div>
            
            <p className="text-secondary-400 text-sm mb-4">{service.description}</p>
            
            {service.metrics && (
              <div className="grid grid-cols-3 gap-2">
                {service.metrics.map((metric, metricIndex) => (
                  <div key={metricIndex} className="text-center">
                    <div className="text-accent-400 font-semibold">{metric.value}</div>
                    <div className="text-secondary-500 text-xs">{metric.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* System Information */}
      <div className="mt-6 bg-secondary-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">System Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-secondary-400">Platform</div>
            <div className="text-white">Linux</div>
          </div>
          <div>
            <div className="text-secondary-400">Architecture</div>
            <div className="text-white">x86_64</div>
          </div>
          <div>
            <div className="text-secondary-400">Python Version</div>
            <div className="text-white">3.11</div>
          </div>
          <div>
            <div className="text-secondary-400">Uptime</div>
            <div className="text-white">12h 34m</div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {overallHealth !== 'healthy' && (
        <div className="mt-6 bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-400 font-semibold">System Alerts</span>
          </div>
          <p className="text-yellow-300 text-sm mt-2">
            {overallHealth === 'degraded' 
              ? 'Some services are experiencing performance issues. Monitor system logs for details.'
              : 'Critical services are unavailable. Check service configurations and restart if necessary.'
            }
          </p>
        </div>
      )}
    </div>
  )
}

export default HealthDashboard
